import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Favorite,
  ShoppingCart,
  FavoriteBorder,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { wishlistAPI, productAPI } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';

const WishlistPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { addToCart, setIsCartOpen } = useCart();

  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userCountry } = useCurrency();

  useEffect(() => {
    // Don't redirect while loading auth state
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchWishlist();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await wishlistAPI.get();

      console.log('Wishlist API Response:', response);

      // Handle different response structures
      let wishlistItems = [];
      if (response.success && response.data) {
        wishlistItems = response.data;
      } else if (Array.isArray(response)) {
        wishlistItems = response;
      }

      console.log('Wishlist Items:', wishlistItems);

      if (!wishlistItems || wishlistItems.length === 0) {
        setWishlistProducts([]);
        return;
      }

      // Transform products - backend returns full product data in productId field
      const transformedProducts = wishlistItems.map((item) => {
        // Product data is nested in productId field
        const product = item.productId;

        if (product && product._id) {
          // Fetch variants separately if needed
          return {
            id: product._id,
            name: product.name,
            category: product.subCategory?.name || 'Uncategorized',
            price: 0, // Will be set when variants are loaded
            mrp: 0,
            image: product.images?.[0] || 'https://via.placeholder.com/500',
            description: product.description || '',
            slug: product.slug,
            variants: [],
            stock: 0,
            rawProduct: product, // Keep original for reference
          };
        }
        return null;
      }).filter(p => p !== null);

      console.log('Transformed Products:', transformedProducts);

      // Fetch full product details including variants
      const productsWithVariants = await Promise.all(
        transformedProducts.map(async (product) => {
          try {
            const allProductsResponse = await productAPI.getAllProducts({ limit: 100, userCountry: userCountry });
            const fullProduct = allProductsResponse.data?.find(p => p._id === product.id);

            if (fullProduct) {
              return {
                ...product,
                price: fullProduct.variants?.[0]?.sellingPrice || 0,
                mrp: fullProduct.variants?.[0]?.mrp || 0,
                variants: fullProduct.variants || [],
                stock: fullProduct.variants?.[0]?.stock || 0,
              };
            }
            return product;
          } catch (err) {
            console.error('Error fetching product variants:', err);
            return product;
          }
        })
      );

      setWishlistProducts(productsWithVariants);

    } catch (err) {
      console.error('Error fetching wishlist:', err);
      setError(err.message || 'Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await wishlistAPI.remove(productId);
      setWishlistProducts(wishlistProducts.filter(p => p.id !== productId));
    } catch (err) {
      console.error('Error removing from wishlist:', err);
    }
  };

  const handleAddToCart = (product) => {
    if (product.variants && product.variants.length > 0) {
      addToCart(product, 1, product.variants[0]);
      setIsCartOpen(true);
    }
  };

  const handleViewProduct = (slug) => {
    navigate(`/products/${slug}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          pt: 12,
          pb: 8,
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pt: { xs: 9, md: 10 },
        pb: 8,
        minHeight: '100vh',
        bgcolor: '#F8F6F1',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          className='font2'
          variant="h3"
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 700,
            mb: 1,
            textAlign: 'center',
          }}
        >
          My Wishlist
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          Your saved products
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {wishlistProducts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FavoriteBorder sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Your wishlist is empty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start adding products you love!
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/shop')}
              sx={{
                borderRadius: 50,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                },
              }}
            >
              Browse Products
            </Button>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {wishlistProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(201, 169, 97, 0.25)',
                    },
                  }}
                >
                  <IconButton
                    className='wish-icon'
                    onClick={() => handleRemoveFromWishlist(product.id)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'white',
                      zIndex: 1,
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <Favorite color="error" />
                  </IconButton>

                  <CardMedia
                    component="img"
                    height="240"
                    image={product.image}
                    alt={product.name}
                    sx={{
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleViewProduct(product.slug)}
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    >
                      {product.category}
                    </Typography>

                    <Typography
                      variant="h6"
                      className='font14'
                      sx={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontWeight: 600,
                        mb: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleViewProduct(product.slug)}
                    >
                      {product.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                        }}
                      >
                        ₹{product.price}
                      </Typography>
                      {product.mrp && product.mrp > product.price && (
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.secondary',
                          }}
                        >
                          ₹{product.mrp}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<ShoppingCart />}
                      onClick={() => handleAddToCart(product)}
                      disabled={product.stock <= 0}
                      sx={{
                        borderRadius: 50,
                        py: 1,
                        background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                        },
                      }}
                    >
                      {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default WishlistPage;
